/**
 * Página de Información Económico-Financiera - Portal de Transparencia
 * Información financiera y contable de INTERCASA, S.A.
 * Estilo Awwwards con animaciones GSAP y diseño inmersivo
 */

import React, { useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ImmersiveLayout from '../../components/layout/ImmersiveLayout';
import {
  AnimatedSection,
  ScrollProgress,
  PageTransition
} from '../../components/animations';

/**
 * Página de Información Económico-Financiera
 * @returns {JSX.Element} Página de información económico-financiera
 */
const EconomicInfoPage = () => {
  const location = useLocation();

  /**
   * Efecto para hacer scroll al inicio de la página al cargar
   */
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.key]);

  return (
    <ImmersiveLayout>
      {/* Hero Section - Estilo Portal Transparencia */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        <div className="relative z-10 container mx-auto px-4 md:px-8">
          <div className="max-w-4xl">
            {/* Link de regreso */}
            <Link 
              to="/transparencia"
              className="group inline-flex items-center text-white/60 hover:text-white mb-8 transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium tracking-wide">Portal de Transparencia</span>
            </Link>
            
            <h1 className="font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 drop-shadow-lg">
              Información<br />
              <span className="text-intercasa-yellow">Económico-Financiera</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              Transparencia en nuestra gestión contable y financiera
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-5xl mx-auto">
          
            {/* Información del Ejercicio */}
            <AnimatedSection animation="fadeUp" className="mb-16">
              <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                Ejercicio 2024
              </span>
              <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl mb-8">
                Información relativa al ejercicio 2024
              </h2>
              
              <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone mb-8">
                <p className="text-intercasa-dark/70 leading-relaxed mb-6">
                  Para acceder al <strong className="text-intercasa-dark">Informe de Auditoría</strong> y a las <strong className="text-intercasa-dark">Cuentas Anuales Abreviadas</strong> correspondientes al ejercicio anual terminado el 31 de diciembre de 2024, puede obtener dicha documentación a través de la página oficial de Registradores de España, en el siguiente enlace:
                </p>
                
                <a 
                  href="https://sede.registradores.org/sede/sede-corpme-web/home" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-intercasa-red hover:underline font-medium mb-6"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  sede.registradores.org
                </a>
                
                <p className="text-intercasa-dark/70 leading-relaxed mb-6">
                  Dentro de la página, deberá seleccionar la opción <strong className="text-intercasa-dark">"Información contable – Depósito de cuentas"</strong>.
                </p>
                
                <p className="text-intercasa-dark/70 leading-relaxed">
                  Asimismo, puede solicitar esta información enviando un correo electrónico a la siguiente dirección: <a href="mailto:notificaciones@grumasa.es" className="text-intercasa-red hover:underline font-medium">notificaciones@grumasa.es</a>
                </p>
              </div>

              {/* Botones de descarga */}
              <div className="flex flex-wrap gap-4 justify-center mb-8">
                <a 
                  href="/PortalTansparenciaIntercasa/InformacionEconomi/InformacionEconomico-Financiera.pdf" 
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Descargar PDF
                </a>
                <a
                  href="/PortalTansparenciaIntercasa/InformacionEconomi/InformacionEconomico-Financiera.odt"
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Descargar ODT
                </a>
              </div>
            </AnimatedSection>

          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default EconomicInfoPage;